import { Cloud, HardDrive, KeyRound, Plus, ShieldAlert, ShieldCheck, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ActionMenu } from '@/modules/backups/ActionMenu';
import { SETUP_CARD_GRID } from '@/modules/backups/setup-layout';
import { endpointHost, providerForDestination } from '@/modules/backups/storage-providers';
import { flatPrimary, innerSurface, toneWash } from '@/components/surface/tones';
import { IconTile, SectionHeader } from '@/components/surface/ui';
import type { BackupDestination, BackupSchedule } from '@/types/api';

/**
 * Where backups go, each destination set out as the resource it is.
 *
 * Drawn as the second half of the block that opens with what is backed up
 * ({@see SourcesSection}); the page places the block.
 *
 * ## Three things about a destination, in the order they matter
 *
 * 1. **What it is** — the provider and region, recognised from the endpoint it was saved with.
 * 2. **How well it protects** — offsite, or on the server being backed up — as a band of its own,
 *    in green or amber, with the consequence written out: this is the fact a customer most needs
 *    and most often assumes.
 * 3. **The details** — bucket or directory, endpoint, credentials, and which sources' schedules
 *    put their copies there — in a quiet two-column list, for when they are wanted.
 *
 * ## A local destination is not a backup of the server
 *
 * It survives a bad change and a deleted table; it does not survive the disk, the VPS or the
 * provider. Said on the destination itself, because somebody who assumes otherwise finds out on the
 * worst day.
 *
 * Nothing secret is shown. `has_secret` says whether a key is stored; the key itself has no read
 * path anywhere in the platform, and the access key id is not needed to recognise a destination.
 */
export function DestinationsSection({
  destinations,
  schedules,
  permitsChanges,
  removing,
  onAdd,
  onRemove,
  className,
}: {
  destinations: BackupDestination[];
  schedules: BackupSchedule[];
  permitsChanges: boolean;
  removing: boolean;
  onAdd: () => void;
  onRemove: (id: string) => void;
  className?: string | undefined;
}) {
  return (
    <div id="storage" className={cn('min-w-0 scroll-mt-24', className)}>
      <SectionHeader
        icon={Cloud}
        iconTone="primary"
        title="Backup storage"
        description="Pushed to cloud storage you own, or kept on the server to download."
        actions={
          destinations.length > 0 ? (
            <Button variant="ghost" size="sm" onClick={onAdd} disabled={!permitsChanges} className="text-muted-foreground hover:text-foreground">
              <Plus />
              Add destination
            </Button>
          ) : undefined
        }
      />

      {destinations.length === 0 ? (
        <div className="px-5 pb-5">
          <p className="text-[13px] text-muted-foreground">
            Add a destination before you can take a backup. Cloud storage you own keeps copies that
            survive losing the server.
          </p>
          <Button size="sm" onClick={onAdd} disabled={!permitsChanges} className={cn('mt-3', flatPrimary)}>
            <Plus />
            Add destination
          </Button>
        </div>
      ) : (
        <ul className={SETUP_CARD_GRID}>
          {destinations.map((destination) => (
            <DestinationCard
              key={destination.id}
              destination={destination}
              usedBy={schedules
                .filter((schedule) => schedule.destination_id === destination.id)
                .map((schedule) => schedule.source_name)}
              removing={removing}
              onRemove={onRemove}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function DestinationCard({
  destination,
  usedBy,
  removing,
  onRemove,
}: {
  destination: BackupDestination;
  /** The sources whose schedules put their copies here. */
  usedBy: string[];
  removing: boolean;
  onRemove: (id: string) => void;
}) {
  const s3 = destination.kind === 's3';
  const offsite = destination.is_offsite;
  const provider = providerForDestination(destination.kind, destination.endpoint);
  const type = provider?.label ?? destination.kind_label;
  const schedulesUsing = destination.used_by_schedules;

  return (
    <li className={cn(innerSurface, 'flex flex-col')}>
      {/* What it is. */}
      <div className="flex items-start gap-3 p-3.5 pb-3">
        <IconTile icon={offsite ? Cloud : HardDrive} tone={offsite ? 'primary' : 'warning'} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground">{destination.name}</p>
          <p className="truncate text-xs text-muted-foreground">
            {type}
            {s3 && destination.region !== '' && ` · ${destination.region}`}
          </p>
        </div>
        <ActionMenu
          label={`More actions for ${destination.name}`}
          items={[
            {
              label: 'Remove destination',
              icon: Trash2,
              tone: 'danger',
              hint:
                schedulesUsing > 0
                  ? `Used by ${schedulesUsing} schedule${schedulesUsing === 1 ? '' : 's'}. Change or remove ${schedulesUsing === 1 ? 'it' : 'them'} first.`
                  : undefined,
              disabled: removing || schedulesUsing > 0,
              onSelect: () => onRemove(destination.id),
            },
          ]}
        />
      </div>

      {/* How well it protects, and what that means. */}
      <div className={cn('mx-3.5 flex items-start gap-2.5 rounded-md px-3 py-2.5', offsite ? toneWash.success : toneWash.warning)}>
        {offsite ? (
          <ShieldCheck className="mt-px size-4 shrink-0 text-success" aria-hidden />
        ) : (
          <ShieldAlert className="mt-px size-4 shrink-0 text-warning" aria-hidden />
        )}
        <div className="min-w-0">
          <p className={cn('text-[13px] font-semibold leading-5', offsite ? 'text-success' : 'text-warning')}>
            {offsite ? 'Offsite' : 'Server only'}
          </p>
          <p className="text-xs text-foreground/80">
            {offsite
              ? 'Survives losing the server.'
              : 'Lost if the server is lost. Download each backup, or add cloud storage for copies that survive.'}
          </p>
        </div>
      </div>

      {/* The details. */}
      <dl className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 px-3.5 pb-3.5 pt-3 text-xs">
        <Detail label={s3 ? 'Bucket' : 'Directory'} title={destination.summary}>
          <span className="font-mono text-[11px]">{destination.location}</span>
        </Detail>
        {s3 && destination.endpoint !== '' && (
          <Detail label="Endpoint" title={destination.endpoint}>
            <span className="font-mono text-[11px]">{endpointHost(destination.endpoint)}</span>
          </Detail>
        )}
        <Detail label="Credentials">
          {!s3 ? (
            <span className="text-muted-foreground">Not needed</span>
          ) : destination.has_secret ? (
            <span className="inline-flex items-center gap-1.5">
              <KeyRound className="size-3 text-muted-foreground" aria-hidden />
              Stored encrypted
            </span>
          ) : (
            <span className="text-muted-foreground">None stored</span>
          )}
        </Detail>
        <Detail label="Used by" title={usedBy.join(', ')}>
          {usedBy.length > 0 ? (
            usedBy.join(', ')
          ) : schedulesUsing > 0 ? (
            `${schedulesUsing} schedule${schedulesUsing === 1 ? '' : 's'}`
          ) : (
            <span className="text-muted-foreground">No schedule</span>
          )}
        </Detail>
      </dl>
    </li>
  );
}

function Detail({ label, title, children }: { label: string; title?: string | undefined; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 truncate text-foreground/90" title={title}>
        {children}
      </dd>
    </>
  );
}
